<template>
  <p
    v-if="buyFlowStore.isRenewOrUpgrade && (userStore.currentUser || buyFlowStore.userGuid)"
    id="renewalOrUpgradeComputer"
    class="mb-half"
  >
    {{ tn(`backupForComputerName`) }} {{ computerName }}
  </p>
  <p v-else-if="!isWebrootSku" class="text-grey text-sm">
    <i18n-t keypath="NotableFeature.backupForComputer" tag="div" class="mb-half" scope="global">
      <template #quantity>
        {{ devicesProtected }}
      </template>
    </i18n-t>
  </p>
  <p
    v-else-if="isWebrootSku && showNotableFeatureForWebroot && FLAGS.ENABLE_BUYFLOW_WEBROOT"
    id="descriptionForWebroot"
    class="text-grey text-sm"
  >
    <i18n-t :keypath="keyPathForDescription" tag="div" class="mb-half" scope="global">
      <template #quantity>
        {{ devicesProtected }}
      </template>
      <template v-if="hasIdentity" #identityCount>
        {{ identityCount }}
      </template>
    </i18n-t>
  </p>
</template>

<script setup lang="ts">
import { t } from "@/i18n";
import { FLAGS } from "@/define";
import { computed, PropType, ref, watch } from "vue";
import { getComputerName } from "@/components/Buy/BuyHelper";
import { ICartItem } from "@/common/api/unifiedPortal/interfaces";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { useUserStore } from "@/stores/user";
import {
  webrootSkusWithNotableFeature,
  webrootMultiDeviceSkus,
  webrootSkusWithIdentity,
} from "@/common/webrootProductName";

const componentName = "NotableFeature";
const userStore = useUserStore();
function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

const props = defineProps({
  cartItem: {
    type: Object as PropType<ICartItem>,
    required: true,
  },
});

const buyFlowStore = useBuyFlowStore();

const computerName = computed(() => {
  return cartItem.value.computerId > 0 ? getComputerName(cartItem.value.computerId) : "";
});

const cartItem = computed(() => {
  return props.cartItem;
});

const isWebrootSku = computed(() => {
  return buyFlowStore.isWebrootSku(cartItem.value.sku);
});

const selectedRatePlanId = computed(() => {
  return cartItem.value.ratePlanId;
});
const product = computed(() => {
  return buyFlowStore.getCatalogItemBySku(cartItem.value.sku);
});

const devicesProtected = computed(() => {
  return product.value?.ratePlans.filter(l => l.ratePlanId == selectedRatePlanId.value)[0].includedUnits || 1;
});

const showNotableFeatureForWebroot = ref<boolean>(webrootSkusWithNotableFeature.some(l => l == cartItem.value.sku));
const hasIdentity = ref<boolean>(webrootSkusWithIdentity.some(l => l == cartItem.value.sku));
const identityCount = ref<number>(cartItem.value.sku.toLowerCase().includes("family") ? 10 : 1);
const keyPathForDescription = ref<string>("");

const getKeyPathForWebrootSkuDescription = (quantity: number) => {
  var showDevicesMessage = webrootMultiDeviceSkus.some(l => l == cartItem.value.sku);
  if (showDevicesMessage) {
    if (webrootSkusWithIdentity.some(l => l == cartItem.value.sku)) {
      keyPathForDescription.value =
        identityCount.value > 1
          ? "NotableFeature.protectionForDevicesWithIdentities"
          : "NotableFeature.protectionForDevicesWithIdentity";
    } else {
      keyPathForDescription.value =
        quantity > 1 ? "NotableFeature.protectionForDevices_other" : "NotableFeature.protectionForDevices_one";
    }
  } else {
    keyPathForDescription.value =
      quantity > 1 ? "NotableFeature.protectionForComputer_other" : "NotableFeature.protectionForComputer_one";
  }
};

watch(
  devicesProtected,
  () => {
    getKeyPathForWebrootSkuDescription(devicesProtected.value);
  },
  { immediate: true }
);
</script>
